import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface SessionData {
  phoneNumber: string
  accessToken: string
  expiresAt: number
}

interface SessionContextType {
  session: SessionData | null
  createSession: (phoneNumber: string, accessToken: string, expiresInHours?: number) => void
  clearSession: () => void
  isSessionValid: () => boolean
}

const SESSION_STORAGE_KEY = 'qr_menu_session'
const SESSION_DURATION_HOURS = 24 // Session expires after 24 hours

const SessionContext = createContext<SessionContextType | undefined>(undefined)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionData | null>(() => {
    try {
      const savedSession = localStorage.getItem(SESSION_STORAGE_KEY)
      if (savedSession) {
        const sessionData = JSON.parse(savedSession)
        // Check if session is still valid
        if (sessionData.expiresAt && sessionData.expiresAt > Date.now()) {
          return sessionData
        } else {
          // Session expired, remove it
          localStorage.removeItem(SESSION_STORAGE_KEY)
        }
      }
    } catch (error) {
      console.error('Error loading session from localStorage:', error)
    }
    return null
  })

  const createSession = (phoneNumber: string, accessToken: string, expiresInHours: number = SESSION_DURATION_HOURS) => {
    const expiresAt = Date.now() + (expiresInHours * 60 * 60 * 1000)
    const sessionData: SessionData = {
      phoneNumber,
      accessToken,
      expiresAt
    }
    setSession(sessionData)
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData))
    } catch (error) {
      console.error('Error saving session to localStorage:', error)
    }
  }

  const clearSession = () => {
    setSession(null)
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing session from localStorage:', error)
    }
  }

  const isSessionValid = () => {
    if (!session) return false
    return session.expiresAt > Date.now()
  }

  // Check session validity periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (session && !isSessionValid()) {
        clearSession()
      }
    }, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [session])

  return (
    <SessionContext.Provider value={{
      session,
      createSession,
      clearSession,
      isSessionValid
    }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}

